/**
 * The conjugation, with the endings picked out.
 *
 * This is the reason Verbos is not just Substantivos with different words. A
 * learner who sees `com|o`, `com|e`, `com|emos`, `com|em` is learning one
 * pattern; a learner who sees four whole words is memorising four facts. So the
 * ending carries the emphasis and the stem recedes.
 *
 * Irregular verbs get the same table with no highlight, and a line saying they
 * are irregular. That is the honest rendering: `sou`, `é`, `somos`, `são` have
 * no shared ending to point at, and highlighting the tail of each anyway would
 * teach a pattern that does not exist.
 */
import { cn } from '@/lib/utils';

import { PERSON_ALIASES, PERSON_LABELS, type FormSplit, type VerboPayload } from './types';

function Form({ split }: { split: FormSplit }) {
  return (
    <span className="font-display text-lg">
      <span className="text-muted-foreground">{split.stem}</span>
      <span
        className={cn(
          split.regular ? 'text-primary font-semibold' : 'text-foreground',
          split.regular && 'bg-primary/10 rounded-sm px-0.5',
        )}
      >
        {split.ending}
      </span>
    </span>
  );
}

export function ConjugationTable({ payload }: { payload: VerboPayload }) {
  const { present, preterite, gerund } = payload.conjugation;
  // A verb is irregular when its forms had to be compared rather than ruled.
  // The gerund is regular even for irregular verbs, so it does not get a vote.
  const irregular = present.some((f) => !f.regular);

  return (
    <div className="border-border flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex items-baseline justify-between">
        <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
          Conjugação
        </span>
        {irregular && (
          <span className="text-muted-foreground text-xs">irregular — no ending to learn</span>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr>
              <th className="w-16" />
              <th className="text-muted-foreground pb-1 text-xs font-medium tracking-wide uppercase">
                agora
              </th>
              <th className="text-muted-foreground pb-1 text-xs font-medium tracking-wide uppercase">
                ontem
              </th>
            </tr>
          </thead>
          <tbody>
            {PERSON_LABELS.map((person, i) => {
              // 140 of the 153 verbs in this deck have a `nós` form identical
              // in both tenses — every regular verb does. The table prints the
              // same word twice and looks like a bug, so it says so in place.
              // A note on the card would have been a note on 92% of the cards.
              const same =
                present[i].stem + present[i].ending === preterite[i].stem + preterite[i].ending;
              return (
                <tr key={person}>
                  <td className="text-muted-foreground py-1 pr-3 align-top text-sm">
                    <div>{person}</div>
                    {PERSON_ALIASES[i] && (
                      <div className="text-muted-foreground/70 text-xs">{PERSON_ALIASES[i]}</div>
                    )}
                  </td>
                  <td className="py-1 pr-4 align-top">
                    <Form split={present[i]} />
                  </td>
                  <td className="py-1 align-top">
                    <Form split={preterite[i]} />
                    {same && (
                      <div className="text-muted-foreground text-xs">
                        same in both — context tells
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="border-border flex items-baseline gap-2 border-t pt-2">
        <span className="text-muted-foreground text-xs tracking-wide uppercase">gerúndio</span>
        <Form split={gerund} />
      </div>
    </div>
  );
}
